import { Box, Typography, Divider } from '@mui/material';
import {
  AdModel,
  AdMyModel,
  PaginationType,
  State,
  CollectionsModel,
  NotificationTypeEnum,
} from '@/types';
import styles from './Favorites.module.scss';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { Loader, Pagination } from '@/components';
import { EmptyList } from './EmptyList';
import { AdsListItem } from '@/components/Ads/AdsListItem';
import { favoritesProvider, collectionsProvider } from '@/providers';
import { CATALOG_ADS_PER_PAGE } from '@/components/Ads/constants';
import { useChangeQueryParams } from '@/hooks';
import queryString from 'query-string';
import { AdsGetFavoritesResponseType } from '@/types/responses';
import { CollectionItem } from './CollectionItem';
import { DeleteDialog, RenameDialog } from './FavoritesDialogs';
import { appSetNotification } from '@/store';

export const Favorites = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(({ auth }: State) => auth);
  const [ads, setAds] = useState<AdModel[] | AdMyModel[]>([]);
  const [paginationObj, setPaginationObj] = useState<PaginationType>();
  const changeQueryParams = useChangeQueryParams('');
  const [isLoadingGetAds, setIsLoadingGetAds] = useState(false);
  const [isLoadingGetCollections, setIsLoadingGetCollections] = useState(false);
  const [isLoadingUpdateCollections, setIsLoadingUpdateCollections] = useState(false);
  const { search: queryParamsStr } = useLocation();
  const queryParams = queryString.parse(queryParamsStr);
  const [collections, setCollections] = useState<CollectionsModel[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<{
    id: string;
    titleText: string;
    dialogType: 'delete' | 'rename';
  } | null>(null);

  const getAds = useCallback(async () => {
    setIsLoadingGetAds(true);
    const newOffset = queryParams.page
      ? (parseInt(queryParams.page as string) - 1) * CATALOG_ADS_PER_PAGE
      : 0;
    const newPaginationObj = {
      page: parseInt(queryParams.page as string) || 1,
      offset: newOffset || 0,
      limit: CATALOG_ADS_PER_PAGE,
      total: 0,
    };
    try {
      const { data } = await favoritesProvider.getFavorites(newPaginationObj);
      const { items, total } = data as AdsGetFavoritesResponseType;
      setAds(items ?? []);
      setPaginationObj({
        ...newPaginationObj,
        total,
      });
    } catch (err) {
      console.log(err);
    }
    setIsLoadingGetAds(false);
  }, []);

  const getCollections = async () => {
    setIsLoadingGetCollections(true);
    const { ok, data, status, message } = await collectionsProvider.getCollections();
    if (ok && Array.isArray(data)) {
      setCollections(data);
    } else {
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
    }
    setIsLoadingGetCollections(false);
  };

  const handleChangePage = (page: number) => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    changeQueryParams({
      ...queryParams,
      page,
    });
  };

  useEffect(() => {
    void getAds();
    getCollections();
  }, []);

  const handleOpenDialog = (id: string, titleText: string, dialogType: 'delete' | 'rename') => {
    setSelectedCollection({ id, titleText, dialogType });
    dialogType === 'delete' ? setOpenDeleteDialog(true) : setOpenEditDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false);
    setOpenEditDialog(false);
    setSelectedCollection(null);
  };

  const deleteCollection = async () => {
    if (!selectedCollection) return;
    setIsLoadingUpdateCollections(true);
    const { ok, status, message } = await collectionsProvider.deleteCollection(
      selectedCollection.id
    );
    if (ok) {
      getCollections();
    } else {
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
    }
    setIsLoadingUpdateCollections(false);
    handleCloseDialog();
  };

  const renameCollection = async (value: string, id: string) => {
    setIsLoadingUpdateCollections(true);
    const payload = { collectionName: value, id };
    const { ok, status, message } = await collectionsProvider.updateCollection(payload);
    if (ok) {
      getCollections();
    } else {
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
    }
    setIsLoadingUpdateCollections(false);
    handleCloseDialog();
  };

  if (isLoadingGetAds || isLoadingGetCollections || isLoadingUpdateCollections) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      <Box className={styles.title}>
        <span className={styles.text}>Favorites / Collections</span>
      </Box>
      {collections.length > 0 && (
        <>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#2A3842',
              margin: '10px 0',
            }}
          >
            Collections
          </Typography>
          <Box className={styles.collectionsContainer}>
            {collections.map((collection) => (
              <CollectionItem
                key={collection.id}
                collection={collection}
                onSelectionClick={handleOpenDialog}
              />
            ))}
          </Box>
          <Divider sx={{ border: `2px solid #E9EEF1`, width: '100%' }} />
        </>
      )}
      {collections.length > 0 && (
        <Typography
          sx={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#2A3842',
            margin: '10px 0',
          }}
        >
          Favorites
        </Typography>
      )}
      {!ads?.length ? (
        <EmptyList />
      ) : (
        <div className={styles.adsList}>
          {(ads as AdModel[]).map((item) => (
            <AdsListItem
              key={item.id}
              item={item}
              tariffPlan={user.tariffPlan?.name}
              type='favorite'
            />
          ))}
        </div>
      )}
      {!!paginationObj?.total && paginationObj?.total > CATALOG_ADS_PER_PAGE ? (
        <div className={styles.paginationContainer}>
          <Pagination paginationObj={paginationObj} onChangePage={handleChangePage} fullWidth />
        </div>
      ) : null}
      {selectedCollection && selectedCollection.dialogType === 'delete' && (
        <DeleteDialog
          title='Are you sure you want to delete the folder?'
          open={openDeleteDialog}
          onClose={handleCloseDialog}
          onClick={deleteCollection}
        />
      )}
      {collections && selectedCollection && selectedCollection.dialogType === 'rename' && (
        <RenameDialog
          collections={collections}
          id={selectedCollection.id}
          name={selectedCollection.titleText}
          open={openEditDialog}
          onClose={handleCloseDialog}
          renameCollection={renameCollection}
        />
      )}
    </div>
  );
};
